import type { LinksFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import {
  isRouteErrorResponse,
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useMatches,
  useRouteError } from
'@remix-run/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/shared';
import i18next from '~/i18next.server';
import i18nCookie from './i18n-cookie';
import type { User } from '~/services/auth.server';
import { checkAuth } from '~/services/auth.server';
import * as Sentry from '@sentry/remix';
import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import { ExternalScripts } from '~/components/ExternalScripts';
import TopBar from '~/components/TopBar';
import SideNav from '~/components/SideNav';
import CatchBoundaryContainer from '~/components/CatchBoundaryContainer';
import {
  createAffiliateCxdCookie,
  createAffiliateMetaDataCookie,
  createAffiliateRefCookie,
  createAffiliateSourceIdCookie,
  createCorporateCookie,
  createSalesTagCookie,
  createUtmCookie,
  createVoucherCookie,
  getAffiliateCxdCookie,
  getAffiliateMetaDataCookieParams,
  getAffiliateRefCookieParams,
  getAffiliateSourceIdCookieParams } from
'~/cookies';
import { NS } from './enums';
import styles from 'styles/app.css';
import favicon32 from 'public/favicon-32.png';
import favicon48 from 'public/favicon-48.png';
import favicon256 from 'public/favicon-256.png';
import ttpLogo from 'public/images/logo-dark.svg';
import errorImage from 'public/images/error.svg';
import type { Language } from './types/api-client';
import { getDisplayLanguages } from './services/languages.server';
import { getHiddenMenuItems } from '~/services/menu-items.server';
import { commitSession, getSession } from '~/services/session.server';
import ApiClient from './services/api-client.server';
import { ChallengeDataProvider } from './components/ChallengeDataProvider';
import { buildTabTitle, removeFirstSubdomain } from './helpers/string';
import { AppConfig } from './AppConfig';

export const links: LinksFunction = () => {
  return [
  { rel: 'icon', type: 'image/png', href: favicon32, sizes: '32x32' },
  { rel: 'icon', type: 'image/png', href: favicon48, sizes: '48x48' },
  { rel: 'icon', type: 'image/png', href: favicon256, sizes: '256x256' },
  { rel: 'stylesheet', href: styles }];

};

export const meta: MetaFunction = () => {
  return [
  { charset: 'utf-8' },
  { title: buildTabTitle(AppConfig.TabDefaultPrefix) },
  { name: 'viewport', content: 'width=device-width,initial-scale=1' }];

};

type LoaderData = {
  locale: string;
  user: User | null;
  mainDomainUrl: string;
  displayLanguages: Language[];
  hiddenMenuItems: String[];
};

export let loader: LoaderFunction = async ({ request }) => {
  let locale = await i18next.getLocale(request);
  // Send user without tokens

  // const auth = await getAuth(request)
  const auth = await checkAuth(request, new Headers(), {}, false);

  const apiClient = new ApiClient(auth?.accessToken, request.headers);

  // TODO tokens should be removed automatically on AuthProfile json serialization
  let user = auth ?
  {
    email: auth.email,
    clientId: auth.clientId,
    clientType: auth.clientType,
    clientContact: auth.clientContact,
    displayClientId: auth.displayClientId
  } :
  null;

  const url = new URL(request.url);
  const headers = new Headers();

  // Get session and set the locale
  const session = await getSession(request.headers.get('cookie'));

  if (auth && !session.get('first_access')) {
    locale = auth?.locale?.slice(0, 2) ?? 'en';
    session.set('first_access', true);
  }

  // Commit the session and append the Set-Cookie header
  headers.append('Set-Cookie', await commitSession(session));

  // i18nCookie
  headers.append('Set-Cookie', await i18nCookie.serialize(locale));

  //Affiliate Source cookie - NO REPLACE
  if (url.searchParams.get('tap_s') && getAffiliateSourceIdCookieParams(request.headers) === null) {
    const affiliateSourceCookie = createAffiliateSourceIdCookie(url, request.headers);
    if (affiliateSourceCookie) headers.append('Set-Cookie', affiliateSourceCookie);
  }

  //Affiliate MetaData cookie - NO REPLACE
  let affiliateMetaDataObj: any = null;
  if (getAffiliateMetaDataCookieParams(request.headers) === null) {
    //check for any tm_* url params
    const affiliateMetaData = createAffiliateMetaDataCookie(url, request.headers);
    if (affiliateMetaData?.cookie) {
      headers.append('Set-Cookie', affiliateMetaData.cookie);
      affiliateMetaDataObj = affiliateMetaData.params;
    }
  }

  // AffiliateRef cookie - NO REPLACE
  if (url.searchParams.get('ref') && getAffiliateRefCookieParams(request.headers) === null) {
    const affiliateRefCookie = createAffiliateRefCookie(url, request.headers);
    if (affiliateRefCookie) headers.append('Set-Cookie', affiliateRefCookie);
  }

  // AffiliateCxd cookie - NO REPLACE
  if (url.searchParams.get('cxd') && getAffiliateCxdCookie(request.headers) === null) {
    const affiliateCxdCookie = createAffiliateCxdCookie(url, request.headers);
    if (affiliateCxdCookie) headers.append('Set-Cookie', affiliateCxdCookie);
  }

  // Utm cookie
  const utmCookie = createUtmCookie(url, request.headers);
  if (utmCookie) headers.append('Set-Cookie', utmCookie);

  //Sales tag cookie
  const salesTagCookie = createSalesTagCookie(url, request.headers);
  if (salesTagCookie) headers.append('Set-Cookie', salesTagCookie);

  // Voucher cookie
  const voucherCookie = createVoucherCookie(url, request.headers);
  if (voucherCookie) headers.append('Set-Cookie', voucherCookie);

  // Corporate cookie
  const corporateCookie = createCorporateCookie(url, request.headers);
  if (corporateCookie) headers.append('Set-Cookie', corporateCookie);

  const displayLanguages = await getDisplayLanguages(request, headers);
  const hiddenMenuItems = auth?.clientId ? await getHiddenMenuItems(request, headers) : [];

  //get dynamically the main domain URL
  const updatedUrl = removeFirstSubdomain(url.origin);
  const mainDomainUrl = (process.env.APP_ENV ?? '') === 'local' ? 'https://www.thetradingpit.com' : updatedUrl;

  return json<LoaderData>(
    {
      locale,
      user,
      mainDomainUrl: mainDomainUrl ?? '',
      displayLanguages,
      hiddenMenuItems
    },
    {
      headers: headers
    }
  );
};

/**
 * Temporary workaround for i18n.changeLanguage is not a function
 * @see https://github.com/sergiodxa/remix-i18next/issues/107
 */
export function useChangeLanguage(locale: string) {
  let { i18n } = useTranslation();
  useEffect(() => {
    i18n.changeLanguage(locale);
  }, [locale, i18n]);
}

/*** IMPORTANT: We need to load all the namespaces here otherwise they don't load properly! ***/
export let handle = { i18n: [NS.GLOBALS, NS.UI, NS.CHALLENGES, NS.ORDERS_PAYMENTS, NS.PERSONAL_INFO, NS.TOOLS] };

function App() {
  const { user, locale, mainDomainUrl, displayLanguages, hiddenMenuItems } = useLoaderData<LoaderData>();
  const { i18n } = useTranslation();

  // Navbar Open/Closed State
  const [isNavOpen, setNavOpen] = useState(false);
  const handleNavToggle = () => setNavOpen(!isNavOpen);
  const location = useLocation(); // Get the current route path
  const hideSideNav = location.pathname.startsWith('/affiliate');

  // set the i18n instance language to the current locale
  useChangeLanguage(locale);

  return (
    <html lang={locale} dir={i18n.dir()}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className={`bg-base-100 text-base-content ${isNavOpen ? 'overflow-hidden lg:overflow-auto' : ''}`}>
        <ExternalScripts />
        <ChallengeDataProvider>
          <TopBar
            user={user}
            locale={locale}
            isNavOpen={isNavOpen}
            navToggle={handleNavToggle}
            languages={displayLanguages} />

          <div id="main" className={`h-full ${user && !hideSideNav ? 'nav-main-visible' : ''}`}>
            {isNavOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} />}
            {user && !hideSideNav &&
            <nav className={`nav-main ${isNavOpen ? 'nav-open' : null} border-brand-base-150  border-r bg-white`}>
                <SideNav
                navToggle={handleNavToggle}
                hiddenMenuItems={hiddenMenuItems}
                locale={locale}
                mainDomainUrl={mainDomainUrl} />

              </nav>
            }
            <div className="workspace h-full">
              <Outlet context={{ mainDomainUrl: mainDomainUrl }} />
            </div>
          </div>
        </ChallengeDataProvider>
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
      </body>
    </html>);

}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError() as Error;
  //console.log(error)

  // Client side ONLY
  if (typeof document !== 'undefined') {
    const matches = useMatches();
    if (matches[0]?.data && matches[0]?.data['user']) {
      let user = matches[0]?.data['user'];

      if (user) Sentry.setUser({ email: user?.email });
    }
  }

  captureRemixErrorBoundaryError(error);

  const { i18n } = useTranslation();
  let errorTitle = i18n.t('error', { ns: NS.UI });
  let message = i18n.t('error_message', { ns: NS.UI });
  // let backToHomepage = i18n.t('back_to_homepage', { ns: NS.GLOBALS })

  // when true, this is what used to go to `CatchBoundary`
  if (isRouteErrorResponse(error)) {
    let mappedMessage = null;

    switch (error.data) {
      case 'QUO-5004':
      case 'BR-70053':
        mappedMessage = i18n.t('error_400acc1', { ns: NS.UI });
        break;
      case 'QUO-5002':
      case 'BR-70052':
        mappedMessage = i18n.t('error_400acc2', { ns: NS.UI });
        break;
      case 'QUO-5003':
        mappedMessage = i18n.t('error_400acc_3', { ns: NS.UI });
        break;
    }

    return (
      <html lang={i18n.language} dir={i18n.dir()}>
        <head>
          <Meta />
          <Links />
        </head>
        <body className="h-screen bg-base-100 text-base-content">
          <div className="fixed left-0 top-0 z-10 w-full border-b border-brand-gray-200 bg-brand-white px-4 py-2.5 sm:px-7.5">
            <Link to="/" className="flex items-center p-1">
              <img src={ttpLogo} className="h-8 w-auto sm:h-10" alt="TTP" />
            </Link>
          </div>

          <div id="main" className="h-full">
            <div className="h-full">
              <div className="flex h-full items-center justify-center py-16 lg:container">
                <div className="text-center">
                  {/* <img src={ttpLogo} className="mx-auto h-10 w-auto" alt="ttp" /> */}
                  <img src={errorImage} alt="" className="mx-auto max-w-60 rounded-full" />

                  {mappedMessage ?
                  <h1 className="mt-16 text-xl lg:text-2xl">{mappedMessage}</h1> :

                  <CatchBoundaryContainer errorTitle={errorTitle} message={message} caught={error} />
                  }

                  {/* <a href="/" className="btn btn-success-gradient mt-6">
                     <span>{backToHomepage}</span>
                    </a> */}
                </div>
              </div>
            </div>
          </div>
          <Scripts />
        </body>
      </html>);

  }

  return (
    <html lang={i18n.language} dir={i18n.dir()}>
      <head>
        <Meta />
        <Links />
      </head>
      <body className="h-screen bg-base-100 text-base-content">
        <div className="fixed left-0 top-0 z-10 w-full border-b border-brand-gray-200 bg-brand-white px-4 py-2.5 sm:px-7.5">
          <Link to="/" className="flex items-center p-1">
            <img src={ttpLogo} className="h-8 w-auto sm:h-10" alt="TTP" />
          </Link>
        </div>

        <div id="main" className="h-full">
          <div className="h-full">
            <div className="flex h-full items-center justify-center px-4 py-16 lg:container">
              <div className="flex max-w-[400px] flex-col gap-5 text-center text-brand-violet-950">
                <h2 className="text-2xl font-bold md:text-3xl">Server error — hang tight!</h2>
                <img src={errorImage} alt="" className="mx-auto h-[150px] w-[150px] rounded-full" />

                <h3 className="text-lg font-medium md:text-xl">
                  Looks like something went wrong on our end. We’re working on a fix.
                </h3>
                <h3 className="text-sm font-light">
                  Try refreshing or come back in a few minutes. If the problem persists, contact our support team.
                </h3>

                <Button fullWidth onClick={() => window.location.reload()}>
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </div>
        <Scripts />
      </body>
    </html>);

}